import isEqual from 'lodash/isEqual'
import { useRouter } from 'next/router'
import React, { useEffect, useRef, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { FormattedMessage, IntlShape, useIntl } from 'react-intl'
import ReactModal from 'react-modal'
import ModalDefault from 'react-modal'

import s from './styles.module.scss'

import ModalSelectProposalTravelTypeButtonGroup from './components/modal-select-proposal-travel-type-button-group/ModalSelectProposalTravelTypeButtonGroup'
import {
  MODAL_SELECT_PROPOSAL_MODE,
  StepEnum,
} from './modal-select-proposal.enum'
import {
  IModalSelectProposalLogsHook,
  IModalSelectProposalProps,
  ISelectProposalForm,
} from './modal-select-proposal.types'
import ModalSelectProposalClientRequest from './modal-select-proposal-client-request/ModalSelectProposalClientRequest'
import { ModalSelectProposalClientRequestBackofficeLayout } from './modal-select-proposal-client-request-backoffice-layout/ModalSelectProposalClientRequestBackofficeLayout'
import { SelectClientRequest } from './modal-select-proposal-client-request-backoffice-layout/select-client-request/SelectClientRequest'
import { ModalSelectProposalFooter } from './modal-select-proposal-footer/ModalSelectProposalFooter'
import ModalSelectProposalMenus, {
  FormClientPricingMenusOutputDtoRecord,
} from './modal-select-proposal-menus/ModalSelectProposalMenus'
import { ModalSelectProposalSelectOptions } from './modal-select-proposal-select-options/ModalSelectProposalSelectOptions'
import {
  computeFormulaLogMessage,
  computeOptionsLogMessage,
  formatClientPricingItemsFromMenus,
  getHouseExtrasMenus,
  getHouseRegularMenus,
  getSelectProposalPageTitle,
  hasHouseEveningMenu,
  hasHouseExtrasMenus,
  hasMenus,
  isAutonomousProposalInputValid,
  isFormValid,
} from './utils/modal-select-proposal.utils'
import { Capacity } from '../capacity'
import { BackgroundImage, Icon, IconSvg } from '../../atoms'
import {
  CurrencyAreaContextProvider,
  EnvironmentTypeEnum,
  PRICE_STATUS,
  PriceContextProvider,
  useEnvironmentTypeContext,
  useTravelTypeContext,
  VAT_STATUS,
} from '../../contexts'
import { MobileBackPage } from '../../layout'
import { LoginModalTypeEnum } from '../../templates/login-modal/login.enum'
import {
  ArrivalEnum,
  ClientPricingItemInputDto,
  DepartureEnum,
  FilterBudgetTypeEnum,
  LogTypeEnum,
  PriceObjectOutputDto,
  TravelTypeEnum,
} from '../../../lib/graphql'
import useDateFormatter from '../../../lib/hooks/useDateFormatter'
import { errorToast, successToast } from '../../../utils'
import { Mixpanel } from '../../../utils/Mixpanel'
import { pushToGTM } from '../../../utils/pushToGTM'

export interface IFormInput {
  projectName: string
}

const prevStepTitle = ({ step, intl }: { step: StepEnum; intl: IntlShape }) => {
  switch (step) {
    case StepEnum.SELECT_OPTIONS:
      return intl.formatMessage({
        id: 'ui.prevStepTitle.backToSuggestion',
        defaultMessage: 'Retour aux suggestions',
      })
    case StepEnum.SELECT_MENU:
      return intl.formatMessage({
        id: 'ui.prevStepTitle.backToOptions',
        defaultMessage: 'Retour aux options',
      })
    case StepEnum.SELECT_EXTRA:
      return intl.formatMessage({
        id: 'ui.prevStepTitle.backToPreviousStep',
        defaultMessage: "Retour à l'étape précédente",
      })
    case StepEnum.SELECT_CLIENT_REQUEST:
      return intl.formatMessage({
        id: 'ui.prevStepTitle.backToPreviousStep',
        defaultMessage: "Retour à l'étape précédente",
      })
  }
}

export const ModalSelectProposal = ({
  setModalProps,
  house,
  dates,
  adults,
  individualBedroomAdults,
  sharedBedroomAdults,
  setSnackbar,
  oneDayTrip = false,
  mode = MODAL_SELECT_PROPOSAL_MODE.DEFAULT,
  threadId,
  useSelectProposalHook,
  isLowRes = false,
  setShowDevisForm,
  indexInResults,
  resultScores,
  sessionId,
  searchId,
  backoffice = false,
  filterBudgetType,
  fromMap = false,
}: IModalSelectProposalProps) => {
  const { query: urlParams } = useRouter()
  const dateFormatter = useDateFormatter()
  const { travelType, setTravelType } = useTravelTypeContext()
  const {
    isAuthenticated,
    isFirstRender,
    setLoginModalType,
    useModalSelectProposalLogsHook,
    getClientPricingMenus,
    createAutonomouseClientProposal,
    isCreateProposalLoading,
    getBookingPrice,
    checkTokenOwnerAccess,
    getClientRequests,
    clientRequests,
    getHouseAvailabilitiesFromTo,
    houseAvailabilities,
  } = useSelectProposalHook()

  const intl = useIntl()

  const environment = useEnvironmentTypeContext()

  const [page, setPage] = useState<StepEnum>(
    !oneDayTrip ? StepEnum.SELECT_OPTIONS : StepEnum.SELECT_MENU,
  )
  const [vatStatus, setVatStatus] = useState<VAT_STATUS>(VAT_STATUS.HT)
  const [totalUser, setTotalUser] = useState<
    Pick<PriceObjectOutputDto, 'priceWithDiscountAndFees'> | undefined
  >(undefined)
  const [isOwner, setIsOwner] = useState<boolean>(false)

  const isStudyDayDisabled = !house.options.studyDay.isEnabled
  const isNightlyTripDisabled = !house.options.nightlyTrip.isEnabled
  const isCorporateEveningEventDisabled =
    !house.options.corporateEvening.isEnabled

  let createLog: IModalSelectProposalLogsHook['createLog'] | undefined
  if (useModalSelectProposalLogsHook) {
    const { createLog: createLogHookFunction } = useModalSelectProposalLogsHook(
      { houseId: house._id },
    )
    createLog = createLogHookFunction
  }

  const getDefaultArrival = ({
    travelType,
  }: {
    travelType: TravelTypeEnum
  }) => {
    return travelType !== TravelTypeEnum.CORPORATE_EVENING_EVENT
      ? ArrivalEnum.MORNING
      : undefined
  }

  const getDefaultDeparture = ({
    travelType,
  }: {
    travelType: TravelTypeEnum
  }) => {
    if (travelType === TravelTypeEnum.NIGHTLY_TRIP) {
      return DepartureEnum.MORNING
    } else if (travelType === TravelTypeEnum.DAILY_TRIP) {
      return DepartureEnum.EVENING
    }
    return undefined
  }

  const methods = useForm<ISelectProposalForm>({
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues: {
      arrival: getDefaultArrival({ travelType }),
      departure: getDefaultDeparture({ travelType }),
      dates: { startDate: dates.startDate, endDate: dates.endDate },
      individualBedroomAdults,
      sharedBedroomAdults,
      adults,
      menus: {},
      restauration: filterBudgetType
        ? filterBudgetType === FilterBudgetTypeEnum.ALL_INCLUSIVE
        : travelType !== TravelTypeEnum.CORPORATE_EVENING_EVENT,
      hours: {
        arrivalHour: undefined,
        departureHour: undefined,
      },
    },
  })

  const {
    control,
    getValues,
    setValue,
    watch,
    reset,
    handleSubmit,
    setError,
    clearErrors,
    formState: { errors },
  } = methods

  const handleQuantityError = (key: string, message: string | null) => {
    if (message) {
      setError(key as keyof ISelectProposalForm, {
        type: 'manual',
        message,
      })
    } else {
      clearErrors(key as keyof ISelectProposalForm)
    }
  }

  const fetchClientPricingMenus = async () => {
    try {
      const res = await getClientPricingMenus({
        dto: {
          houseId: house._id,
          startDate: getValues().dates.startDate,
          endDate: getValues().dates.endDate,
          adults: Number(getValues().adults),
          individualBedroomAdults: Number(getValues().individualBedroomAdults),
          sharedBedroomAdults: Number(getValues().sharedBedroomAdults),
          arrival: getValues().arrival,
          departure: getValues().departure,
          restauration: Boolean(getValues().restauration),
          travelType,
        },
      })

      if (res) {
        setValue(
          'menus',
          res.reduce(
            (acc: FormClientPricingMenusOutputDtoRecord, clientPricingMenu) => {
              acc[clientPricingMenu._id] = clientPricingMenu
              return acc
            },
            {},
          ),
        )
      }
    } catch (e) {
      errorToast([
        intl.formatMessage({
          id: 'ui.ModalSelectProposal.fetchClientPricingMenus.error',
          defaultMessage:
            'Une erreur est survenue, veuillez réessayer ultérieurement',
        }),
      ])
    }
  }

  const lastCallArgsRef = useRef<ClientPricingItemInputDto[] | null>(null)
  useEffect(() => {
    const args = formatClientPricingItemsFromMenus(getValues())
    const sameArgsAsLastCall =
      lastCallArgsRef.current && isEqual(lastCallArgsRef.current, args)
    if (sameArgsAsLastCall) {
      return
    }
    lastCallArgsRef.current = args
    const fetchBookingPriceAndSetTotalUser = async (
      args: ClientPricingItemInputDto[],
    ) => {
      const res = await getBookingPrice({
        // this can never be called before ref is set and it is never reset
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        clientPricingItems: args,
        houseId: house._id,
      })
      const resultsAreForLatestCall = args === lastCallArgsRef.current
      if (res && resultsAreForLatestCall) {
        setTotalUser(res.totalUser ?? undefined)
      }
    }

    if (
      isAuthenticated &&
      isFormValid({ house, form: watch(), page, travelType })
    ) {
      fetchBookingPriceAndSetTotalUser(lastCallArgsRef.current)
    }
  }, [isAuthenticated, house, watch(), travelType])

  useEffect(() => {
    if (
      house.isAutonomousBookingAvailable &&
      isFormValid({ house, form: watch(), page, travelType }) &&
      isAuthenticated
    ) {
      fetchClientPricingMenus()
    }
  }, [
    watch('adults'),
    watch('dates'),
    watch('arrival'),
    watch('departure'),
    watch('sharedBedroomAdults'),
    watch('individualBedroomAdults'),
    watch('restauration'),
    isAuthenticated,
  ])

  useEffect(() => {
    //only used from yodai
    if (house.isAutonomousBookingAvailable && oneDayTrip) {
      fetchClientPricingMenus()
    }
  }, [oneDayTrip])

  useEffect(() => {
    if (!isFirstRender) {
      reset({
        adults,
        dates: {
          startDate: undefined,
          endDate: undefined,
        },
        arrival: getDefaultArrival({ travelType }),
        departure: getDefaultDeparture({ travelType }),
        restauration: true,
      })
      setTotalUser(undefined)
    }
  }, [travelType])

  useEffect(() => {
    const computeTravelType = (travelType?: TravelTypeEnum): TravelTypeEnum => {
      if (
        travelType === TravelTypeEnum.CORPORATE_EVENING_EVENT &&
        !isCorporateEveningEventDisabled
      ) {
        return TravelTypeEnum.CORPORATE_EVENING_EVENT
      }

      if (travelType === TravelTypeEnum.DAILY_TRIP && !isStudyDayDisabled) {
        return TravelTypeEnum.DAILY_TRIP
      }

      if (
        travelType === TravelTypeEnum.NIGHTLY_TRIP &&
        !isNightlyTripDisabled
      ) {
        return TravelTypeEnum.NIGHTLY_TRIP
      }

      if (!isNightlyTripDisabled) {
        return TravelTypeEnum.NIGHTLY_TRIP
      } else if (!isStudyDayDisabled) {
        return TravelTypeEnum.DAILY_TRIP
      } else if (!isCorporateEveningEventDisabled) {
        return TravelTypeEnum.CORPORATE_EVENING_EVENT
      } else if (travelType) {
        return travelType
      } else {
        return TravelTypeEnum.NIGHTLY_TRIP
      }
    }

    setTravelType(computeTravelType(urlParams.travelType as TravelTypeEnum))
  }, [house])

  const handleCreateProposal = async () => {
    const values = getValues()

    if (
      !isAutonomousProposalInputValid({
        values,
        travelType,
        priceType: house.priceType,
        isAutonomousBookingAvailable,
        intl,
      })
    ) {
      return
    }

    const res = await createAutonomouseClientProposal({
      dto: {
        travelType,
        threadId,
        houseId: house._id,
        adults: values.adults ?? 0,
        selectedClientPricingItemsMenu: formatClientPricingItemsFromMenus(
          values,
        )
          .map((item) => {
            return {
              type: item.type,
              objectId: item.option.objectId,
              quantity: item.priceOption.quantity,
            }
          })
          .filter((item) => item.quantity > 0),
        dates: {
          startDate: values.dates.startDate,
          endDate: values.dates.endDate,
        },
        ...(travelType === TravelTypeEnum.CORPORATE_EVENING_EVENT && {
          hours: {
            arrivalHour: values.hours.arrivalHour,
            departureHour: values.hours.departureHour,
          },
        }),
        ...(values.clientRequestId
          ? { clientRequestId: values.clientRequestId }
          : { briefTitle: values.briefTitle }),
        ...(travelType === TravelTypeEnum.NIGHTLY_TRIP && {
          arrival: values.arrival,
          departure: values.departure,
          individualBedroomAdults: Number(values.individualBedroomAdults),
          sharedBedroomAdults: Number(values.sharedBedroomAdults),
          restauration: Boolean(values.restauration),
        }),
        isAutonomousBookingAvailable,
        indexInResults,
        resultScores,
        sessionId,
        searchId,
        fromMap,
      },
    })

    if (res) {
      if (values.clientRequestId) {
        pushToGTM({ event: 'popup-resa-auto-ajout' })
      } else {
        pushToGTM({ event: 'popup-resa-auto-creation-devis' })
      }

      const briefTitle =
        values.briefTitle ??
        clientRequests?.find(({ _id }) => _id === values.clientRequestId)
          ?.briefTitle ??
        intl.formatMessage({
          id: 'ui.ModalSelectProposal.briefTitle',
          defaultMessage: 'séminaire',
        })

      setSnackbar?.({
        message: briefTitle,
        imageURL: house.coverPicture.uri ?? '',
        clientRequestId: res._id,
      })
      setPage(StepEnum.SELECT_OPTIONS)
      setModalProps(undefined)
      if (setShowDevisForm) {
        setShowDevisForm(false)
      }
      if (environment === EnvironmentTypeEnum.BACKOFFICE) {
        successToast(
          intl.formatMessage({
            id: 'ui.ModalSelectProposal.successCreatedQuote',
            defaultMessage: 'Devis créé avec succès',
          }),
          'top-left',
        )
      }
    }
  }

  if (typeof window !== 'undefined') {
    ReactModal.setAppElement('body')
  }

  const sendCloseEvent = () => {
    switch (page) {
      case StepEnum.SELECT_OPTIONS:
        pushToGTM({ event: 'closed-popup-resa-auto-1' })
        break
      case StepEnum.SELECT_MENU:
        pushToGTM({ event: 'closed-popup-resa-auto-2' })
        break
      case StepEnum.SELECT_CLIENT_REQUEST:
        pushToGTM({ event: 'closed-popup-resa-auto-3' })
        break
    }
  }

  const closeModal = () => {
    sendCloseEvent()
    setPage(StepEnum.SELECT_OPTIONS)
    setModalProps(undefined)
  }

  const handlePrevStepChange = () => {
    switch (page) {
      case StepEnum.SELECT_OPTIONS:
        closeModal()
        break
      case StepEnum.SELECT_MENU:
        if (oneDayTrip) {
          closeModal()
        } else {
          setPage(StepEnum.SELECT_OPTIONS)
        }
        break
      case StepEnum.SELECT_EXTRA:
        setPage(StepEnum.SELECT_MENU)
        break
      case StepEnum.SELECT_CLIENT_REQUEST:
        if (hasHouseExtrasMenus({ formValues: getValues() })) {
          setPage(StepEnum.SELECT_EXTRA)
        } else {
          setPage(StepEnum.SELECT_MENU)
        }
        break
    }
  }

  const moveToNextStep = () => {
    if (page === StepEnum.SELECT_OPTIONS) {
      const newPage =
        isAutonomousBookingAvailable &&
        travelType !== TravelTypeEnum.CORPORATE_EVENING_EVENT
          ? StepEnum.SELECT_MENU
          : StepEnum.SELECT_CLIENT_REQUEST
      pushToGTM({ event: 'continuer-popup-resa-auto-formules-choices' })
      if (createLog) {
        createLog({
          type: LogTypeEnum.SELECT_QUOTATION_OPTIONS,
          messages: computeOptionsLogMessage(
            getValues(),
            dateFormatter,
            travelType,
          ),
        })
      }
      setPage(newPage)
    } else if (page === StepEnum.SELECT_MENU) {
      // Can not continue if no menu are available
      if (!hasMenus({ menus: getValues('menus') })) {
        return
      }

      pushToGTM({ event: 'continuer-popup-resa-auto-project-choice' })
      if (createLog) {
        createLog({
          type: LogTypeEnum.SELECT_QUOTATION_FORMULA,
          messages: computeFormulaLogMessage({
            menus: getHouseRegularMenus({ formValues: getValues() }),
          }),
        })
      }
      if (hasHouseExtrasMenus({ formValues: getValues() })) {
        setPage(StepEnum.SELECT_EXTRA)
      } else {
        setPage(StepEnum.SELECT_CLIENT_REQUEST)
      }
    } else if (page === StepEnum.SELECT_EXTRA) {
      pushToGTM({ event: 'continuer-popup-resa-auto-3' })
      if (createLog) {
        createLog({
          type: LogTypeEnum.SELECT_QUOTATION_EXTRA,
          messages: computeFormulaLogMessage({
            menus: getHouseExtrasMenus({ formValues: getValues() }),
          }),
        })
      }
      if (
        environment === EnvironmentTypeEnum.BACKOFFICE ||
        clientRequests?.length
      ) {
        setPage(StepEnum.SELECT_CLIENT_REQUEST)
      } else {
        // set the value of the briefTitle to the default value
        setValue(
          'briefTitle',
          intl.formatMessage({
            id: 'ui.ModalSelectProposal.moveToNextStep.myNextSeminar',
            defaultMessage: 'Mon prochain séminaire',
          }),
        )
        // set the value of the clientRequestId to undefined so API creates a new project
        setValue('clientRequestId', undefined)
        // go next automatically
        handleCreateProposal()
      }
    } else if (page === StepEnum.SELECT_CLIENT_REQUEST) {
      handleCreateProposal()
    }
  }

  const handleNextStepChange = () => {
    moveToNextStep()
  }

  const isAutonomousBookingAvailable =
    house.isAutonomousBookingAvailable ?? false

  useEffect(() => {
    const isOwnerViewingOwnHouse = async () => {
      if (
        checkTokenOwnerAccess &&
        urlParams.ownerToken &&
        typeof urlParams.ownerToken === 'string'
      ) {
        const res = await checkTokenOwnerAccess({ token: urlParams.ownerToken })
        setIsOwner(res)
      }
    }
    isOwnerViewingOwnHouse()
  }, [])

  useEffect(() => {
    if (setLoginModalType) {
      if (isAuthenticated || isOwner) {
        setLoginModalType({ type: LoginModalTypeEnum.Close })
      }
    }
  }, [isAuthenticated, isOwner])

  if (isLowRes) {
    return (
      <FormProvider {...methods}>
        <MobileBackPage
          backPageProps={{
            title: (
              <FormattedMessage
                id='ui.ModalSelectProposal.getQuoteMobile'
                defaultMessage='Obtenir un devis'
              />
            ),
          }}
          isModal={true}
          navBar={
            <div className={s.devisFormNavbar}>
              {page !== StepEnum.SELECT_OPTIONS && (
                <div
                  className={s.backIcon}
                  onClick={() => handlePrevStepChange()}
                >
                  <IconSvg.ArrowLeft
                    color='black'
                    style={{ height: '25px', width: '25px' }}
                  />
                </div>
              )}

              <div className={s.navbarTitle}>
                <FormattedMessage
                  id='ui.ModalSelectProposal.getQuote'
                  defaultMessage='Obtenir un devis'
                />
              </div>
              {page == StepEnum.SELECT_OPTIONS && (
                <div
                  className={s.closeModal}
                  onClick={() => {
                    if (setShowDevisForm) {
                      setShowDevisForm(false)
                    }
                  }}
                >
                  <Icon
                    name='cross-without-circle'
                    color='black'
                    height='12px'
                    width='12px'
                  />
                </div>
              )}
            </div>
          }
        >
          <CurrencyAreaContextProvider
            context={{ currencyArea: house.currencyArea }}
          >
            <PriceContextProvider
              toggleStatus={{
                priceStatus: PRICE_STATUS.USER,
                vatStatus,
              }}
            >
              {mode === MODAL_SELECT_PROPOSAL_MODE.IN_HOUSE_CARD &&
                page == StepEnum.SELECT_OPTIONS && (
                  <div className={s.houseCardContainer}>
                    <div
                      className={s.houseCardFormContainer}
                      id='houseCardFormContainer'
                    >
                      <div className={s.formHeader}>
                        <div className={s.houseImage}>
                          <BackgroundImage
                            src={house.coverPicture.uri}
                            alt=''
                            type='cover'
                            className={s.cardImage}
                          />
                        </div>
                        <div className={s.houseDescription}>
                          <div className={s.houseTitle}>{house.title}</div>
                          <div className={s.houseLocation}>
                            {house?.address?.department &&
                              house?.address?.region &&
                              `${house?.address?.department}, ${house?.address?.region}`}
                          </div>
                          <div className={s.houseCapacity}>
                            <Capacity
                              numberOfWorkspaces={house.numberOfWorkspaces ?? 0}
                              dailyMaxCapacity={
                                house.options.studyDay.options?.capacity.max
                              }
                              corporateEveningEventMaxCapacity={
                                house.options?.corporateEvening.options
                                  ?.capacity.max
                              }
                              beds={house.numberOfBeds ?? 0}
                              bedrooms={house.numberOfBedrooms ?? 0}
                              capacity={house.capacity ?? 0}
                              className={s.capacity}
                            />
                          </div>
                        </div>
                      </div>

                      <div className={s.travelTypeItem}>
                        <h4 className={s.instantQuotationTitle}>
                          {isAutonomousBookingAvailable ? (
                            <FormattedMessage
                              id='ui.ModalSelectProposal.createInstantQuote'
                              defaultMessage='Créer mon devis instantané'
                            />
                          ) : (
                            <FormattedMessage
                              id='ui.ModalSelectProposal.createQuote'
                              defaultMessage='Créer mon devis'
                            />
                          )}
                        </h4>
                        <h5>
                          <FormattedMessage
                            id='ui.ModalSelectProposal.stayFormat'
                            defaultMessage='Format séjour'
                          />
                        </h5>

                        <ModalSelectProposalTravelTypeButtonGroup
                          houseOptions={house.options}
                          isMobile={true}
                        />
                      </div>

                      <ModalSelectProposalSelectOptions
                        control={control}
                        getValues={getValues}
                        house={house}
                        mode={mode}
                        errors={errors}
                        isFirstRender={isFirstRender}
                        getHouseAvailabilitiesFromTo={
                          getHouseAvailabilitiesFromTo
                        }
                        houseAvailabilities={houseAvailabilities}
                      />

                      <ModalSelectProposalFooter
                        setVatStatus={setVatStatus}
                        page={page}
                        total={totalUser}
                        adults={getValues().adults}
                        handleNextStepChange={handleSubmit(
                          handleNextStepChange,
                        )}
                        isButtonLoading={isCreateProposalLoading}
                        mode={MODAL_SELECT_PROPOSAL_MODE.DEFAULT}
                        isButtonActive={Object.keys(errors).length === 0}
                        isAutonomousBookingAvailable={
                          isAutonomousBookingAvailable &&
                          travelType !== TravelTypeEnum.CORPORATE_EVENING_EVENT
                        }
                        isAuthenticated={isAuthenticated}
                        isLowRes={true}
                        backoffice={backoffice}
                      />
                    </div>
                  </div>
                )}

              {(mode === MODAL_SELECT_PROPOSAL_MODE.DEFAULT ||
                page !== StepEnum.SELECT_OPTIONS) && (
                <div className={s.houseCardContainer}>
                  <div
                    className={s.houseCardFormContainer}
                    id='houseCardFormContainer'
                  >
                    <div className={s.header}>
                      <div className={s.title}>
                        <h3>
                          {getSelectProposalPageTitle({
                            page,
                            environment,
                            intl,
                          })}
                        </h3>
                      </div>
                      {page === StepEnum.SELECT_MENU && (
                        <div className={s.helpContainer}>
                          <IconSvg.IconInfoFilled
                            width={24}
                            height={24}
                            viewBox='0 0 15 15'
                            color='#1067EB'
                          />
                          <p>
                            <FormattedMessage
                              id='ui.ModalSelectProposal.addProvisions'
                              defaultMessage='Vous souhaitez ajouter des prestations à la carte ? Un conseiller Naboo se tient à votre
                          disposition sur le chat ci-contre !'
                            />
                          </p>
                        </div>
                      )}

                      <div className={s.content}>
                        {page === StepEnum.SELECT_OPTIONS && (
                          <ModalSelectProposalSelectOptions
                            control={control}
                            house={house}
                            mode={mode}
                            errors={errors}
                            isFirstRender={isFirstRender}
                            getHouseAvailabilitiesFromTo={
                              getHouseAvailabilitiesFromTo
                            }
                            houseAvailabilities={houseAvailabilities}
                            getValues={getValues}
                          />
                        )}
                        {page === StepEnum.SELECT_MENU && (
                          <ModalSelectProposalMenus
                            formValues={getValues()}
                            control={control}
                            handleQuantityError={handleQuantityError}
                            type='REGULAR'
                          />
                        )}
                        {page === StepEnum.SELECT_EXTRA && (
                          <ModalSelectProposalMenus
                            formValues={getValues()}
                            control={control}
                            type='EXTRA'
                          />
                        )}
                        {page === StepEnum.SELECT_CLIENT_REQUEST && (
                          <>
                            {environment === EnvironmentTypeEnum.APP ? (
                              <ModalSelectProposalClientRequest
                                control={control}
                                house={house}
                                briefTitle={getValues('briefTitle')}
                                setValue={setValue}
                                clientRequests={clientRequests}
                              />
                            ) : (
                              <ModalSelectProposalClientRequestBackofficeLayout
                                elements={
                                  <>
                                    <SelectClientRequest
                                      control={control}
                                      getClientRequests={getClientRequests}
                                    />
                                  </>
                                }
                              />
                            )}
                          </>
                        )}
                      </div>

                      <ModalSelectProposalFooter
                        setVatStatus={setVatStatus}
                        page={page}
                        total={totalUser}
                        adults={getValues().adults}
                        handleNextStepChange={handleSubmit(
                          handleNextStepChange,
                        )}
                        isButtonLoading={isCreateProposalLoading}
                        mode={MODAL_SELECT_PROPOSAL_MODE.DEFAULT}
                        isButtonActive={Object.keys(errors).length === 0}
                        isAutonomousBookingAvailable={
                          isAutonomousBookingAvailable &&
                          travelType !== TravelTypeEnum.CORPORATE_EVENING_EVENT
                        }
                        isAuthenticated={isAuthenticated}
                        isLowRes={true}
                      />
                    </div>
                  </div>
                </div>
              )}
            </PriceContextProvider>
          </CurrencyAreaContextProvider>
        </MobileBackPage>
      </FormProvider>
    )
  }

  if (
    isStudyDayDisabled &&
    isNightlyTripDisabled &&
    isCorporateEveningEventDisabled
  ) {
    return (
      <ModalDefault
        isOpen={true}
        onRequestClose={closeModal}
        className={s.modal}
        overlayClassName={s.overlay}
      >
        <Icon name='calendar-disabled' size={75} color='var(--clr-grey-200)' />
        <span className={s.bookingDisabledText}>
          <FormattedMessage
            id='ui.ModalSelectProposal.bookingNotAvailable'
            defaultMessage="La réservation n'est pas encore disponible sur ce lieu car l'hôte procède actuellement à des modifications."
          />
        </span>
      </ModalDefault>
    )
  }

  return (
    <FormProvider {...methods}>
      <CurrencyAreaContextProvider
        context={{ currencyArea: house.currencyArea }}
      >
        <PriceContextProvider
          toggleStatus={{
            priceStatus: PRICE_STATUS.USER,
            vatStatus,
          }}
        >
          {mode === MODAL_SELECT_PROPOSAL_MODE.IN_HOUSE_CARD && (
            <div className={s.houseCardContainer}>
              <div
                className={s.houseCardFormContainer}
                id='houseCardFormContainer'
              >
                <div className={s.travelTypeItem}>
                  <h4 className={s.instantQuotationTitle}>
                    {isAutonomousBookingAvailable ? (
                      <FormattedMessage
                        id='ui.ModalSelectProposal.createInstantQuote'
                        defaultMessage='Créer mon devis instantané'
                      />
                    ) : (
                      <FormattedMessage
                        id='ui.ModalSelectProposal.createQuote'
                        defaultMessage='Créer mon devis'
                      />
                    )}
                  </h4>
                  <h5>
                    <FormattedMessage
                      id='ui.ModalSelectProposal.stayFormat'
                      defaultMessage='Format séjour'
                    />
                  </h5>

                  <div
                    data-gtm='toggle-sr-je'
                    onClick={() => Mixpanel.track('toggle-sr-je')}
                  >
                    <ModalSelectProposalTravelTypeButtonGroup
                      houseOptions={house.options}
                    />
                  </div>
                </div>

                <ModalSelectProposalSelectOptions
                  control={control}
                  house={house}
                  mode={mode}
                  errors={errors}
                  isFirstRender={isFirstRender}
                  getHouseAvailabilitiesFromTo={getHouseAvailabilitiesFromTo}
                  houseAvailabilities={houseAvailabilities}
                  getValues={getValues}
                />

                <ModalSelectProposalFooter
                  setVatStatus={setVatStatus}
                  page={page}
                  total={totalUser}
                  adults={getValues().adults}
                  handleNextStepChange={handleSubmit(handleNextStepChange)}
                  isButtonLoading={isCreateProposalLoading}
                  mode={mode}
                  isButtonActive={
                    isAuthenticated && Object.keys(errors).length === 0
                  }
                  isAutonomousBookingAvailable={
                    isAutonomousBookingAvailable &&
                    travelType !== TravelTypeEnum.CORPORATE_EVENING_EVENT
                  }
                  isAuthenticated={isAuthenticated}
                />
              </div>
            </div>
          )}

          {(mode === MODAL_SELECT_PROPOSAL_MODE.DEFAULT ||
            page !== StepEnum.SELECT_OPTIONS) && (
            <ModalDefault
              isOpen={true}
              onRequestClose={closeModal}
              className={s.modal}
              overlayClassName={s.overlay}
            >
              <div className={s.header}>
                <div className={s.returnButton}>
                  {isAutonomousBookingAvailable && (
                    <div onClick={handlePrevStepChange}>
                      <IconSvg.ArrowLeft color='#aaaaaa' />
                      <p>
                        {oneDayTrip && page === StepEnum.SELECT_MENU
                          ? prevStepTitle({
                              step: StepEnum.SELECT_OPTIONS,
                              intl,
                            })
                          : prevStepTitle({ step: page, intl })}
                      </p>
                    </div>
                  )}
                </div>
                <div className={s.title}>
                  <h3>
                    {getSelectProposalPageTitle({ page, environment, intl })}
                  </h3>
                </div>
                <div className={s.closeButton}>
                  <div>
                    <IconSvg.Close
                      onClick={closeModal}
                      style={{ cursor: 'pointer' }}
                      width={10}
                      height={10}
                    />
                  </div>
                </div>
              </div>
              <div className={s.content}>
                {page === StepEnum.SELECT_OPTIONS && (
                  <ModalSelectProposalSelectOptions
                    control={control}
                    house={house}
                    mode={mode}
                    errors={errors}
                    isFirstRender={isFirstRender}
                    getHouseAvailabilitiesFromTo={getHouseAvailabilitiesFromTo}
                    houseAvailabilities={houseAvailabilities}
                    getValues={getValues}
                  />
                )}
                {page === StepEnum.SELECT_MENU && (
                  <ModalSelectProposalMenus
                    formValues={getValues()}
                    control={control}
                    handleQuantityError={handleQuantityError}
                    type='REGULAR'
                  />
                )}
                {page === StepEnum.SELECT_EXTRA && (
                  <ModalSelectProposalMenus
                    formValues={getValues()}
                    control={control}
                    type='EXTRA'
                  />
                )}
                {page === StepEnum.SELECT_CLIENT_REQUEST && (
                  <>
                    {environment === EnvironmentTypeEnum.APP ? (
                      <ModalSelectProposalClientRequest
                        control={control}
                        house={house}
                        briefTitle={getValues('briefTitle')}
                        setValue={setValue}
                        clientRequests={clientRequests}
                      />
                    ) : (
                      <ModalSelectProposalClientRequestBackofficeLayout
                        elements={
                          <>
                            <SelectClientRequest
                              control={control}
                              getClientRequests={getClientRequests}
                            />
                          </>
                        }
                      />
                    )}
                  </>
                )}
              </div>
              {(page === StepEnum.SELECT_MENU ||
                (page === StepEnum.SELECT_EXTRA &&
                  hasHouseEveningMenu({ formValues: getValues() }))) && (
                <div className={s.helpContainer}>
                  <IconSvg.IconInfoFilled
                    width={24}
                    height={24}
                    viewBox='0 0 15 15'
                    color='var(--clr-primary-400)'
                  />
                  <p>
                    {page === StepEnum.SELECT_MENU ? (
                      <FormattedMessage
                        id='ui.ModalSelectProposal.addProvisions'
                        defaultMessage='Vous souhaitez ajouter des prestations à la carte ? Un conseiller Naboo se tient à votre disposition sur le chat ci-contre !'
                      />
                    ) : (
                      <FormattedMessage
                        id='ui.ModalSelectProposal.eveningServiceMandatory'
                        defaultMessage='La prestation de soirée est obligatoire si vous souhaitez prolonger la soirée après le diner.'
                      />
                    )}
                  </p>
                </div>
              )}
              <div className={s.footer}>
                <ModalSelectProposalFooter
                  setVatStatus={setVatStatus}
                  page={page}
                  total={totalUser}
                  adults={getValues().adults}
                  handleNextStepChange={handleSubmit(handleNextStepChange)}
                  isButtonLoading={isCreateProposalLoading}
                  mode={MODAL_SELECT_PROPOSAL_MODE.DEFAULT}
                  isButtonActive={
                    Object.keys(errors).length === 0 &&
                    (page !== StepEnum.SELECT_MENU ||
                      hasMenus({ menus: getValues('menus') }))
                  }
                  isAutonomousBookingAvailable={
                    isAutonomousBookingAvailable &&
                    travelType !== TravelTypeEnum.CORPORATE_EVENING_EVENT
                  }
                  isAuthenticated={isAuthenticated}
                />
              </div>
            </ModalDefault>
          )}
        </PriceContextProvider>
      </CurrencyAreaContextProvider>
    </FormProvider>
  )
}
